import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Input, Card, Space, Flex, Table, Button, Alert, Popconfirm, Spin } from "antd";
import { LeftOutlined, RightOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';
import _ from 'lodash-es';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import Billing from '../../../libs/Billing';
import Evisit from "../../../libs/Evisit";
import request from "../../../libs/request";

export const PaymentContext = createContext();

export const PaymentProvider = ({ children }) => {
    const [workflowData, setWorkflowData] = useState({
        paymentData: {},
        intakeData: {},
        roomData: {},
        component: {}
    });

    const [proccessAddCard, setProccessAddCard] = useState({
        start: false,
        response: null
    });

    const value = {
        workflowData,
        setWorkflowData,
        proccessAddCard,
        setProccessAddCard
    };

    return (
        <PaymentContext.Provider value={value}>
            {children}
        </PaymentContext.Provider>
    );
};

const renderNotice = (room) => {
    if (!_.get(room, "livemode", true)) {
        return <Alert
                    message={intl.get("pay_test_mode_hint").d("This waiting room is under test payment mode. No real money will be deducted from your account. Please use 4242 4242 4242 4242 4242 as your credit card number.")}
                    type="info"
                />;
    }
    return (<></>);
}

const useStripeInstance = () => {
    const [stripe, setStripe] = useState(null);
    //Layouts/default.ctp:69
    const stripeKey = Evisit.c('Account.livemode', false) 
        ? Evisit.c('Settings.stripe_public.publishable')
        : Evisit.c('Settings.stripe_public.dev.publishable');

    useEffect(() => {
        const initializeStripe = async () => {
            const stripeInstance = await loadStripe(stripeKey);
            setStripe(stripeInstance);
        };
        initializeStripe();
    }, [stripeKey]);

    return stripe;
};

const useGetCards = (roomCode) => {
    const [paymentCards, setPaymentCards] = useState(null);
    const [loading, setLoading] = useState(false);
    const [defaultCardId, setDefaultCardId] = useState(null);
    const [refetchIndex, setRefetchIndex] = useState(0);

    const refreshCards = async (roomCode) => {
        setLoading(true);
        const response = await request.get(`${window.base_url}/api_v3/billing/sources`, {
            params: {
                room_code: roomCode
            }
        }).catch((err) => {
            console.error(err);
            setLoading(false);
        })
        setPaymentCards(response.data);
        const defaultCard = _.find(response.data, function(o) {
            return o.is_default;
        });
        const defaultCardId = defaultCard && defaultCard.id ? defaultCard.id : null;
        setDefaultCardId(defaultCardId);
        setLoading(false);
    }

    useEffect(() => {
        if (roomCode) {
            refreshCards(roomCode);
        }
    }, [roomCode, refetchIndex]);

    const refetch = useCallback(() => {
        setRefetchIndex(prevIndex => prevIndex + 1);
    }, []);


    return { paymentCards, loading, refetch, defaultCardId };
}

const CreditCardForm = ({ onCardAdded }) => {
    const {workflowData, proccessAddCard, setProccessAddCard} = useContext(PaymentContext);
    const {roomData} = workflowData;
    const stripe = useStripe();
    const elements = useElements();
    const [name, setName] = useState(_.get(window, "user.full_name", ""));
    const [loading, setLoading] = useState(false);
    const [cardError, setCardError] = useState(null);

    const addCard = () => {
        setLoading(true);
        stripe.createToken(elements.getElement(CardElement), {name: name}).then(function(res) {
            if(res.error) {
                setLoading(false);
                setProccessAddCard({
                    start: false,
                    response: null
                })
                setCardError({message:intl.get("invalid_card").d("Invalid Card")});
            } else {
                Billing.addCard(res.token.id, roomData.code)
                    .then((response) => {
                        setLoading(false);
                        onCardAdded();
                        setProccessAddCard({
                            start: false,
                            response: response.data
                        })
                    })
                    .catch((err) => {
                        console.error(err);
                        setCardError(_.get(err, 'response.data', {message: 'Unknown Error'}));
                        setProccessAddCard({
                            start: false,
                            response: null
                        })
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        });
    }
    const cardErrorMessage = useMemo(() => _.get(cardError, 'message', ''), [cardError]);

    useEffect(() => {
        if (proccessAddCard.start) {
            addCard();
        }
        // eslint-disable-next-line
    }, [proccessAddCard]);

    return (<>
        <Space direction="vertical" style={{ width: '100%' }}>
            <Input
                addonAfter={<UserOutlined />}
                placeholder={ intl.get("card_name").d('Name on Card') }
                value={name}
                onChange={(e) => setName(e.target.value)}
            />
            <CardElement 
                options={{hidePostalCode: true}}
                onChange={(e) => {
                    if (e.error) {
                        setCardError(e.error);
                    } else {
                        setCardError(null);
                    }
                }}
            />
            { cardErrorMessage && <Alert message={cardErrorMessage} type="error" /> }
        </Space>
        <div className="align-right">
            <Button 
                type="link" 
                disabled={!stripe} 
                onClick={ addCard }
                loading={loading}
            >
                { intl.get("add_card").d("Add Card") }
            </Button>
        </div>
    </>);
}

const CreditCards = () => {
    const {workflowData, setWorkflowData} = useContext(PaymentContext);
    const {roomData, component} = workflowData;
    const {paymentCards, loading, refetch, defaultCardId} = useGetCards(roomData.code);
    const [selectedCardId, setSelectedCardId] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const stripe = useStripeInstance();

    var removable = _.get(component, "option.removable", true);

    const selectCard = (card_id) => {
        setSelectedCardId(card_id);
        setWorkflowData(prevState => ({
            ...prevState,
            paymentData: {
                ...prevState.paymentData,
                card_id: card_id
            }
        }));
    }

    useEffect(() => {
        setShowAddForm((_.get(component, 'option.show_add_card') && !defaultCardId));
        setSelectedCardId(defaultCardId);
        setWorkflowData(prevState => ({
            ...prevState,
            paymentData: {
                ...prevState.paymentData,
                card_id: defaultCardId
            }
        }));
        // eslint-disable-next-line
    }, [defaultCardId]);

    const onRefreshCards = () => {
        refetch();
    }

    const sharedOnCell = (_, index) => {
        if (index === paymentCards.length) {
            return {
                colSpan: 0,
            };
        }
        return {};
    };

    const removeCard = (record) => {
        Billing.removeCard(record.id, roomData.code).then((res) => {
            if (_.get(res, 'data.success', true)) {
                refetch();
            } else {

            }
        });
    }

    const columns = () => {
        var tableColumns = [
            {
                title: intl.get("name").d("Name"),
                dataIndex: 'name',
                onCell: (_, index) => ({
                    colSpan: index === paymentCards.length ? 5 : 1,
                }),
            },
            {
                title: intl.get("card").d("Card"),
                dataIndex: 'brand',
                onCell: sharedOnCell,
            },
            {
                title: intl.get("num").d("Number"),
                dataIndex: 'last4',
                render: (text) => <>**** **** **** { text }</>,
                onCell: sharedOnCell,
            },
            {
                title: intl.get("exp").d("Expires"),
                dataIndex: 'exp',
                render: (_, record) => <>{ record["exp_month"] + '/' + record["exp_year"]}</>,
                onCell: sharedOnCell,
            }
        ];
        if (removable) {
            tableColumns.push({
                title: "",
                dataIndex: 'x',
                onCell: sharedOnCell,
                render: (_, record) =>
                    <Popconfirm
                        title={ intl.get("rm_card_title").d("Remove Credit Card") }
                        description={ intl.get("rm_card_confirm").d("Are you sure to delete this card?") }
                        onConfirm={() => removeCard(record)}
                        okText={ intl.get("yes").d("Yes") }
                        cancelText={ intl.get("no").d("No") }
                    >
                        <Button type="link"><DeleteOutlined /></Button>
                    </Popconfirm>
            });
        }
        return tableColumns;
    };

    const cardTableData = useMemo(() => {
        if (!paymentCards) {
            return [];
        }
        const cardDatas = paymentCards.map(obj => {
            return {...obj, key: obj.id}
        });
        return _.concat(cardDatas, {
            "key": "add_new_card",
            "name": intl.get("new_credit_card").d("New Credit Card")
        });
    }, [paymentCards]);

    const cardTableFooter = () => {
        if (!showAddForm) {
            return null;
        }
        return <Elements stripe={stripe}>
                   <CreditCardForm onCardAdded={onRefreshCards} />
               </Elements>;
    }

    const rowSelection = {
        onChange: (selectedRowKeys, selectedRows) => {
            if (selectedRowKeys[0] === "add_new_card") {
                setShowAddForm(true);
            }
            selectCard(selectedRowKeys[0]);
        },
        selectedRowKeys: [selectedCardId]
    };

    return (<>
        { loading ?
            <div className="align-center">
                <Spin size="large" />
                <br/><br/>
                <p><strong>{ intl.get("loading").d("Loading") }...</strong></p>
            </div> :
            <div style={{
                overflow: "auto",
                width: "100%"
            }}>
                <Table
                    rowSelection={{
                        type: 'radio',
                        ...rowSelection,
                    }}
                    columns={ columns() }
                    dataSource={cardTableData}
                    pagination={false}
                    {...(showAddForm ? {footer : cardTableFooter} : {})}
                />
            </div>
        }
    </>);
}

const InvoicePreview = ({section}) => {
    const {workflowData} = useContext(PaymentContext);
    const {paymentData, intakeData, roomData} = workflowData;
    var currency = intakeData.paymentCurrency || _.get(roomData, "payment.paymentCurrency", "USD");
    var perVisitDuration = parseFloat(_.get(intakeData, "consultation.duration", window.CONFIG.Billing.PerVisit.duration));
    return (
        <Card
            className="invoice-preview"
            key={section["code"]}
            size="small"
            title={section["name"] ? section["name"] : intl.get("pay_summary ").d("Summary of Charges")}
        >
            <table className="table" aria-describedby="payment_method">
                <tbody>
                    <tr>
                        <td>
                            <span className="payment-item-name">
                                { intl.get("item").d("Item") + ': ' +  _.get(intakeData, "consultation.description", "") }
                            </span>
                        </td>
                        <td className="align-right">
                            { !_.get(window.CONFIG, 'Account.consultation.duration.is_hidden', false)
                                && (intakeData.type === window.CONST.VISIT.TYPE.ECONSULT) &&
                                <span className="payment-item-description">
                                    { perVisitDuration + ' mins' }
                                </span>
                            }
                        </td>
                        <td className="align-right">
                            <span className="payment-item-amount">
                                { currency + ' ' + Number(_.get(intakeData, "consultation.amount", 0)).toFixed(2) }
                            </span>
                        </td>
                    </tr>
                    <tr>
                        <td style={{width: "20%", verticalAlign: "top"}}>
                            <strong>{ intl.get("total_charge").d('Total Charge') }</strong>
                        </td>
                        <td className="align-right" style={{width: "30%"}} colSpan="2">
                            { !_.isEmpty(paymentData.resultStr) && 
                                <span>
                                    <span>
                                        { paymentData.promoObj }
                                    </span>
                                    <span className="text-uppercase">
                                        { ' (- ' + paymentData.amountOffStr + ')' }
                                    </span>
                                    <br/>
                                </span>
                            }
                            <strong>
                                { currency + ' ' + (paymentData.totalAmount - paymentData.amountOff).toFixed(2) }
                            </strong>
                        </td>
                    </tr>
                </tbody>
            </table>
        </Card>
    );
}

const PromoCode = ({section}) => {
    const {workflowData, setWorkflowData} = useContext(PaymentContext);
    const {paymentData, intakeData} = workflowData;
    var currency = intakeData.paymentCurrency;
    const [showPromo, setShowPromo] = useState(false);
    const [promoCode, setPromoCode] = useState(null);
    const [promoError, setPromoError] = useState(null);
    const [applying, setApplying] = useState(false);

    const applyPromo = () => {
        setApplying(true);

        if (!promoCode) {
            setPromoError(intl.get("err_enter_promo").d("Please enter promo code."));
            setApplying(false);
        } else {
            setPromoError(null);
            Billing.getCoupon(promoCode, currency, intakeData.id).then((res) => {
                if (!res.data.valid) {
                    setPromoError(intl.get("invalid_promo").d('Invalid Promo Code.'));
                    setApplying(false);
                    return;
                }
                const payment = _.cloneDeep(paymentData);
                payment.promoObj = res.data.id;
                if (res.data.percent_off) {
                    payment.amountOff = parseFloat(payment.totalAmount * res.data.percent_off / 100).toFixed(2);
                    payment.amountOffStr = currency + ' ' + payment.amountOff;
                    payment.resultStr = res.data.percent_off + '% ' + intl.get("off").d('OFF');
                } else if (res.data.amount_off) {

                    if (res.data.currency.toUpperCase() !== currency.toUpperCase()) {
                        setPromoError(intl.get("cant_use_promo", {val: Evisit.t("provider")}).d('You cannot use this promo code. Please contact your {val} for more information. CODE: Invalid currency.'));
                        setApplying(false);
                        return;
                    }

                    var amount_off = parseFloat(res.data.amount_off / 100).toFixed(2);
                    if (amount_off > payment.totalAmount) {
                        amount_off = payment.totalAmount;
                    }
                    payment.amountOff = amount_off;
                    payment.amountOffStr = res.data.currency + ' ' + amount_off;
                    payment.resultStr = res.data.currency + ' ' + amount_off + ' ' + intl.get("off").d('OFF');
                }
                payment.promo_code = promoCode;
                setWorkflowData(prevState => ({
                    ...prevState,
                    paymentData: {
                        ...prevState.paymentData,
                        ...payment
                    }
                }));

                setApplying(false);
            }).catch((err) => {
                console.error(err);
                setPromoError(intl.get("invalid_promo").d('Invalid Promo Code.'));
            }).finally(() => {
                setApplying(false);
            });
        }
    }

    const revertResult = () => {
        setWorkflowData(prevState => ({
            ...prevState,
            paymentData: {
                ...prevState.paymentData,
                amountOff: 0,
                amountOffStr: "",
                promoObj: "",
                resultStr: ""
            }
        }));
    }

    return (
        <Card key={section["code"]} size="small" className="promo-code">
            {_.isEmpty(paymentData.resultStr) &&
                <div>
                    { !showPromo ?
                        <Button type="link" onClick={() => setShowPromo(true) }>
                            { _.get(section, "extra.link_text", _.get(section, "name", intl.get("promo_code").d("Promo Code"))) }
                        </Button> :
                        <>
                            <Space direction="horizontal" style={{ width: '100%' }} >
                                <Input value={promoCode} onChange={(e) => setPromoCode(e.target.value)}/>
                                <Button
                                    loading={applying}
                                    onClick={() => {
                                        applyPromo();
                                    }} 
                                >
                                    { intl.get("apply").d("APPLY") }
                                </Button>
                            </Space>
                            <div className="error promo-error" style={{color: "#ff0000"}}>
                                { promoError }
                            </div>
                        </>
                    }
                </div>
            }
            {!_.isEmpty(paymentData.resultStr) &&
                <Space>
                    <strong className="text-uppercase">
                        { paymentData.resultStr }
                    </strong>
                    <strong>
                        { intl.get("applied").d("applied") }
                    </strong>
                    <Button onClick={revertResult}>
                        { intl.get("change").d("Change") }
                    </Button>
                </Space>
            }
            { _.has(section, "extra.hint") &&
                <div style={{color:"darkgrey"}}>
                    { section["extra"]["hint"] }
                </div>
            }
        </Card>
    );
}

const Label = ({section}) => {
    return (
        <p className="payment-label" style={section.extra ? section.extra : {}}>
            { section.title }
        </p>
    );
}

const ManageCards = ({section}) => {
    const {workflowData} = useContext(PaymentContext);
    const {paymentData} = workflowData;

    var showCards = (paymentData.totalAmount - paymentData.amountOff > 0) || _.get(window.CONFIG, 'Account.payment.card_info.always_show', false);
    if (!showCards) {
        return (<></>);
    }

    return (
        <Card key={section["code"]} size="small" className="manage-cards" >
            <h5 id="payment_method">
                { section["name"] ? section["name"] : intl.get("pay_method").d('Payment Method') }
            </h5>
            <CreditCards/>
        </Card>
    );
}

const renderPaymentSections = () => {
    var sectionsData = _.get(window.CONFIG, "states.__mobile__.visits.payment.0.sections", [
        {
            "name": intl.get("estimated_charge").d("Estimated Charge"),
            "code": "invoice_preview"
        },
        {
            "name": intl.get("promo").d("Promo code"),
            "code": "promo_code",
            "extra": {
                "link_text": intl.get("promo_here").d("Have a promo code? Enter here")
            }
        },
        {
            "name": intl.get("select_pay").d("Select Payment"),
            "code": "manage_cards"
        },
        {
            "title": intl.get("only_charge_after").d("You will only be charged after the consultation."),
            "code": "label",
            "extra": {
                "textAlign": "center"
            }
        }
    ]);
    var sections = [];
    var sectionComponents = {
        invoice_preview: InvoicePreview,
        promo_code: PromoCode,
        label: Label,
        manage_cards: ManageCards
    }
    for (var i in sectionsData) {
        var code = sectionsData[i]["code"];
        if (!sectionComponents[code]) {
            continue;
        }
        const PaymentComponent = sectionComponents[code];
        sections.push(
            <PaymentComponent
                key={code}
                section={sectionsData[i]}
            />
        );
    }
    return sections;
}

const PaymentOptions = ({currentComponent, onNext, onPrevious, data, isMobileWebview}) => {
    const [loading, setLoading] = useState(false);
    const [messageError, setMessageError] = useState(null);
    const [shouldSubmit, setShouldSubmit] = useState(false);

    const { 
        workflowData,
        setWorkflowData,
        proccessAddCard,
        setProccessAddCard
    } = useContext(PaymentContext);

    const {
        paymentData,
        intakeData,
        roomData,
    } = workflowData;

    useEffect(() => {
        setWorkflowData({
            paymentData: _.get(data, "payment.value", {}),
            intakeData: _.get(data, "intake.value", {}),
            roomData: _.get(data, "room.value", {}),
            component: currentComponent
        });
    }, [data, currentComponent, setWorkflowData]);

    useEffect(() => {
        // listen to the response of adding a new card
        if (proccessAddCard.response) {
            setWorkflowData(prevState => ({
                ...prevState,
                paymentData: {
                    ...prevState.paymentData,
                    card_id: proccessAddCard.response.id
                }
            }));
        }
        // eslint-disable-next-line
    }, [proccessAddCard]);

    useEffect(() => {
        // automatically submit the form after adding a new card
        if (shouldSubmit && paymentData.card_id) {
            if (paymentData.card_id === "add_new_card") {
                return;
            }
            onSubmit();
        }
        // eslint-disable-next-line
    }, [paymentData.card_id, shouldSubmit]);

    useEffect(() => {
        if (paymentData.totalAmount === 0) {
            onNext({});
        }
        // eslint-disable-next-line
    }, [paymentData.totalAmount])

    const processPayment = (data) => {
        Billing.processVisitPayment(intakeData, paymentData).then((authorizeResponse) => {
            setLoading(false)
            onNext({});
        }).catch(() => {setLoading(false)});
    }

    const updateVisitCard = (data) => {
        const roomCode = roomData.code;
        Billing.updateDefaultCard(paymentData.card_id, roomCode).then(() => {
            processPayment(data);
        }).catch(() => {setLoading(false)});
    }

    const onSubmit = () => {
        setLoading(true);
        if (paymentData.card_id === "add_new_card" || !paymentData.card_id) {
            setMessageError(intl.get("select_card").d("Please select a card."));
            setLoading(false);
            return;
        }
        const payload = {};
        if (intakeData.id) {
            payload.intake_id = intakeData.id;
        }
        if (paymentData.promoObj) {
            payload.promo_code = paymentData.promoObj;
        }
        payload.description = _.get(intakeData, "consultation.description", "");
        payload.currency = intakeData.paymentCurrency;
        payload.amount = paymentData.totalAmount;
        payload.duration = parseFloat(_.get(intakeData, "consultation.duration", window.CONFIG.Billing.PerVisit.duration));
        if (paymentData.card_id) {
            updateVisitCard(payload);
        } else {
            processPayment(payload);
        }
    }

    const onAddCardAndSubmit = async () => {
        setProccessAddCard({start: true});
        setShouldSubmit(true);
    }
    
    return (
        <Space className="payment-options" direction="vertical" size="middle" style={{ display: 'flex' }}>
            { messageError && <Alert message={messageError} type="error" /> }
            { renderNotice(roomData) }
            { renderPaymentSections(data) }
            <Flex justify={isMobileWebview ? "center" : "space-between"}>
                { (onPrevious != null && !isMobileWebview) ?
                    <Button
                        type="primary"
                        ghost
                        onClick={ onPrevious }
                    >
                        <LeftOutlined style={{verticalAlign:"middle"}} />
                        { intl.get("back").d("Back") }
                    </Button> : <div></div>
                }
                <Button
                    type="primary"
                    shape={isMobileWebview ? "round" : "default"}
                    size={isMobileWebview ? "large" : "default"}
                    disabled={!paymentData.card_id}
                    onClick={() => paymentData.card_id === "add_new_card" ? onAddCardAndSubmit() : onSubmit()}
                    loading={loading}
                >
                    {intl.get("continue").d("Continue")}
                    <RightOutlined style={{verticalAlign:"middle"}} />
                </Button>
            </Flex>
        </Space>
    );
};

export default function Payment (props) {
    return (
        <PaymentProvider>
            <PaymentOptions {...props} />
        </PaymentProvider>
    );
}
